import { useMemo } from 'react';
import { Divider, Form, InputNumber, Space, Switch, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, DisconnectOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import Table from '@/components/ui/table/Table';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { GhostIconButton } from '@/components/ui/GhostIconButton';
import { Button } from '@components/ui/Button';

/**
 * Classes & subjects tab: table + all class/subject modals.
 * Logic lives in `useTenantClassesSubjectsScreen`.
 */
export default function ClassesSubjectsClasses({ screen }) {
    const {
        campusesLoading,
        campusPickList,
        branchOptions,
        branchNameById,
        filteredClasses,
        loadingClasses,
        loadingSubjects,
        loadingTeachers,
        teacherSelectOptions,
        teacherNameById,
        classColumns,
        classSubjects,
        selectedClass,
        isEditing,
        setIsEditing,
        classModalOpen,
        setClassModalOpen,
        classDetailOpen,
        setClassDetailOpen,
        addSubjectOpen,
        setAddSubjectOpen,
        editSubjectOpen,
        setEditSubjectOpen,
        selectedSubject,
        setSelectedSubject,
        classForm,
        addSubjectForm,
        detailForm,
        editSubjectForm,
        createClassMutation,
        createSubjectMutation,
        updateClassMutation,
        updateSubjectMutation,
        openClassDetail,
        openAddSubjectForClass,
        submitCreateClass,
        submitAddSubjectToClass,
        saveClassEdits,
        saveSubjectEdits,
        openEditSubject,
        confirmDeleteSubject,
        unassignTeacherFromSubject,
        selectModalPopupContainer,
    } = screen;

    const classSubjectColumns = useMemo(
        () => [
            { title: 'Subject', dataIndex: 'name', key: 'name', render: (v) => v || '—' },
            { title: 'Code', dataIndex: 'code', key: 'code', render: (v) => v || '—' },
            {
                title: 'Assigned teacher',
                key: 'assignedTeacherId',
                render: (_, row) => {
                    const id = row?.assignedTeacherId;
                    if (!id) return '—';
                    return teacherNameById.get(String(id)) || id;
                },
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (v) => <Tag color={(v || 'active') === 'active' ? 'green' : 'default'}>{(v || 'active').toUpperCase()}</Tag>,
            },
            {
                title: 'Action',
                key: 'action',
                render: (_, row) => (
                    <Space>
                        <Tooltip title="Edit">
                            <GhostIconButton
                                type="text"
                                onClick={(e) => {
                                    e?.stopPropagation?.();
                                    openEditSubject(row);
                                }}
                                icon={<EditOutlined />}
                                aria-label="Edit subject"
                            />
                        </Tooltip>
                        {row?.assignedTeacherId ? (
                            <Tooltip title="Unassign teacher">
                                <GhostIconButton
                                    type="text"
                                    onClick={(e) => {
                                        e?.stopPropagation?.();
                                        unassignTeacherFromSubject(row);
                                    }}
                                    icon={<DisconnectOutlined />}
                                    aria-label="Unassign teacher from subject"
                                />
                            </Tooltip>
                        ) : null}
                        <Tooltip title="Delete">
                            <GhostIconButton
                                type="text"
                                onClick={(e) => {
                                    e?.stopPropagation?.();
                                    confirmDeleteSubject(row);
                                }}
                                icon={<DeleteOutlined />}
                                aria-label="Delete subject"
                                style={{
                                    color: 'rgba(255, 77, 79, 0.9)',
                                }}
                            />
                        </Tooltip>
                    </Space>
                ),
            },
        ],
        [confirmDeleteSubject, openEditSubject, teacherNameById, unassignTeacherFromSubject]
    );

    return (
        <>
            <Table
                title="Classes"
                columns={classColumns}
                dataSource={filteredClasses}
                loading={loadingClasses}
                pagination
                pageSize={10}
                onRowClick={openClassDetail}
            />

            <AppModal
                title="Create Class"
                open={classModalOpen}
                onCancel={() => setClassModalOpen(false)}
                onOk={submitCreateClass}
                confirmLoading={createClassMutation.isPending}
                okText="Save class"
                size="md"
            >
                <Form form={classForm} layout="vertical" initialValues={{ status: 'active' }}>
                    <Form.Item name="displayName" label="Display name">
                        <AppInput placeholder="e.g. Grade 10 - A" />
                    </Form.Item>
                    {campusesLoading ? (
                        <Typography.Text type="secondary">Loading campuses…</Typography.Text>
                    ) : campusPickList.length ? (
                        <Form.Item
                            name="branchId"
                            label="Campus"
                            rules={[{ required: true, message: 'Select a campus' }]}
                        >
                            <AppSelect
                                placeholder="Select campus"
                                options={branchOptions}
                                getPopupContainer={selectModalPopupContainer}
                            />
                        </Form.Item>
                    ) : (
                        <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                            Add a campus under{' '}
                            <Link to="/tenant/school-time">School Time Configuration</Link> and save it, then you can assign classes to a campus.
                        </Typography.Paragraph>
                    )}
                    <Form.Item name="grade" label="Grade" rules={[{ required: true, message: 'Grade is required' }]}>
                        <AppInput placeholder="e.g. Grade 10" />
                    </Form.Item>
                    <Form.Item name="section" label="Section">
                        <AppInput placeholder="e.g. A" />
                    </Form.Item>
                    <Form.Item name="name" label="Internal name (optional)">
                        <AppInput placeholder="Optional" />
                    </Form.Item>
                    <Form.Item name="fees" label="Monthly tuition fee (optional)">
                        <InputNumber min={0} placeholder="e.g. 5000" style={{ width: '100%' }} size="large" />
                    </Form.Item>
                    <Form.Item name="status" label="Status">
                        <AppSelect
                            options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                            getPopupContainer={selectModalPopupContainer}
                        />
                    </Form.Item>
                </Form>
            </AppModal>

            <AppModal
                title={selectedClass ? `Class: ${selectedClass.displayName || selectedClass.name || selectedClass.id}` : 'Class details'}
                open={classDetailOpen}
                onCancel={() => {
                    setClassDetailOpen(false);
                    setIsEditing(false);
                }}
                footer={
                    <Space>
                        {isEditing ? (
                            <Button type="primary" loading={updateClassMutation.isPending} onClick={saveClassEdits}>
                                Save changes
                            </Button>
                        ) : null}
                    </Space>
                }
                width="86vw"
                style={{ maxWidth: 980 }}
                size="lg"
                className="app-modal-shell--tall-scroll"
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <Typography.Text style={{ fontWeight: 800 }}>Class information</Typography.Text>
                            <Button
                                type="text"
                                $padding="6px 10px"
                                $size="12px"
                                $gap="6px"
                                onClick={() => {
                                    if (!selectedClass) return;
                                    if (isEditing) {
                                        detailForm.setFieldsValue({
                                            displayName: selectedClass?.displayName || '',
                                            grade: selectedClass?.grade || '',
                                            section: selectedClass?.section || '',
                                            name: selectedClass?.name || '',
                                            status: (selectedClass?.status || 'active') === 'active',
                                            branchId: selectedClass?.branchId || undefined,
                                            fees: selectedClass?.fees !== undefined && selectedClass?.fees !== null ? selectedClass.fees : undefined,
                                        });
                                    }
                                    setIsEditing((v) => !v);
                                }}
                                disabled={!selectedClass}
                            >
                                {isEditing ? 'Cancel edit' : 'Edit'}
                            </Button>
                        </div>
                        <Divider style={{ margin: '10px 0 12px' }} />
                        <Form form={detailForm} layout="vertical">
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 }}>
                                <Form.Item name="displayName" label="Display name">
                                    <AppInput disabled={!isEditing} placeholder="e.g. Grade 10 - A" />
                                </Form.Item>
                                <Form.Item name="name" label="Internal name">
                                    <AppInput disabled={!isEditing} placeholder="Optional" />
                                </Form.Item>
                                <Form.Item name="grade" label="Grade" rules={[{ required: true, message: 'Grade is required' }]}>
                                    <AppInput disabled={!isEditing} placeholder="e.g. Grade 10" />
                                </Form.Item>
                                <Form.Item name="section" label="Section">
                                    <AppInput disabled={!isEditing} placeholder="e.g. A" />
                                </Form.Item>
                                {campusPickList.length ? (
                                    isEditing ? (
                                        <Form.Item
                                            name="branchId"
                                            label="Campus"
                                            rules={[{ required: true, message: 'Select a campus' }]}
                                        >
                                            <AppSelect
                                                disabled={!isEditing}
                                                placeholder="Select campus"
                                                options={branchOptions}
                                                getPopupContainer={selectModalPopupContainer}
                                            />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item label="Campus">
                                            <Typography.Text>
                                                {selectedClass?.branchId
                                                    ? branchNameById.get(selectedClass.branchId) || '—'
                                                    : '—'}
                                            </Typography.Text>
                                        </Form.Item>
                                    )
                                ) : null}
                                <Form.Item label="Status">
                                    {isEditing ? (
                                        <Form.Item name="status" noStyle valuePropName="checked">
                                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                        </Form.Item>
                                    ) : (
                                        <Tag color={(selectedClass?.status || 'active') === 'active' ? 'green' : 'default'}>
                                            {(selectedClass?.status || 'active').toUpperCase()}
                                        </Tag>
                                    )}
                                </Form.Item>
                                <Form.Item name="fees" label="Monthly tuition fee">
                                    <InputNumber disabled={!isEditing} min={0} placeholder="e.g. 5000" style={{ width: '100%' }} size="large" />
                                </Form.Item>
                            </div>
                        </Form>
                    </div>

                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                            <Typography.Text style={{ fontWeight: 800 }}>Subjects</Typography.Text>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAddSubjectForClass}
                                $padding="6px 10px"
                                $size="12px"
                                $gap="6px"
                            >
                                Add subject
                            </Button>
                        </div>
                        <Divider style={{ margin: '10px 0 12px' }} />
                        <Table
                            title={null}
                            columns={classSubjectColumns}
                            dataSource={classSubjects}
                            loading={loadingSubjects}
                            pagination
                            pageSize={10}
                            showSearch={false}
                            showFilter={false}
                            showMore={false}
                        />
                    </div>
                </div>
            </AppModal>

            <AppModal
                title="Add Subject"
                open={addSubjectOpen}
                onCancel={() => setAddSubjectOpen(false)}
                onOk={submitAddSubjectToClass}
                confirmLoading={createSubjectMutation.isPending}
                okText="Save subject"
                size="md"
            >
                <Form form={addSubjectForm} layout="vertical" initialValues={{ status: 'active' }}>
                    <Form.Item name="name" label="Subject name" rules={[{ required: true, message: 'Subject name is required' }]}>
                        <AppInput placeholder="e.g. Mathematics" />
                    </Form.Item>
                    <Form.Item name="code" label="Subject code">
                        <AppInput placeholder="e.g. MATH-101" />
                    </Form.Item>
                    <Form.Item name="status" label="Status">
                        <AppSelect
                            options={[{ value: 'active', label: 'Active' }, { value: 'inactive', label: 'Inactive' }]}
                            getPopupContainer={selectModalPopupContainer}
                        />
                    </Form.Item>
                    <Form.Item name="assignedTeacherId" label="Assign teacher (optional)">
                        <AppSelect
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select teacher"
                            options={teacherSelectOptions}
                            loading={loadingTeachers}
                            getPopupContainer={selectModalPopupContainer}
                        />
                    </Form.Item>
                </Form>
            </AppModal>

            <AppModal
                title="Edit Subject"
                open={editSubjectOpen}
                onCancel={() => {
                    setEditSubjectOpen(false);
                    setSelectedSubject(null);
                }}
                onOk={saveSubjectEdits}
                confirmLoading={updateSubjectMutation.isPending}
                okText="Save changes"
                size="md"
            >
                <Form form={editSubjectForm} layout="vertical" initialValues={{ status: true }}>
                    <Form.Item name="name" label="Subject name" rules={[{ required: true, message: 'Subject name is required' }]}>
                        <AppInput placeholder="e.g. Mathematics" />
                    </Form.Item>
                    <Form.Item name="code" label="Subject code">
                        <AppInput placeholder="e.g. MATH-101" />
                    </Form.Item>
                    <Form.Item label="Status">
                        <Form.Item name="status" noStyle valuePropName="checked">
                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                        </Form.Item>
                    </Form.Item>
                    <Form.Item name="assignedTeacherId" label="Assign teacher (optional)">
                        <AppSelect
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Select teacher"
                            options={teacherSelectOptions}
                            loading={loadingTeachers}
                            getPopupContainer={selectModalPopupContainer}
                        />
                    </Form.Item>
                    {selectedSubject?.assignedTeacherId ? (
                        <Typography.Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 0 }}>
                            <Typography.Link onClick={() => unassignTeacherFromSubject(selectedSubject)}>
                                Unassign teacher now
                            </Typography.Link>
                            {' '}
                            (saves immediately; use Save changes for other fields.)
                        </Typography.Paragraph>
                    ) : null}
                </Form>
            </AppModal>
        </>
    );
}
